import _ from 'lodash';
import {DateTime} from 'luxon';
import React, {FC, Fragment} from 'react';

import {I18n} from '../../../../../libs/shared/helpers/src/internationalization/i18n';
import {I18nProvider, Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {build12Or24hOptionsForFormat, TimeFormatsEnum} from '../../../../helpers/dateTimeHelpers';
import {
  SchedulingLinkAvailabilityModel,
  SchedulingLinkStandardAvailabilityBlockModel,
} from '../../../../models/api/schedulingLinkModel';
import {MeetingTimesData} from './meetingsettings/meetingSettingsPanel';

/*
 * Constants.
 */

export const schedulingContainerStyle = `position: relative;`;

/*
 * Props.
 */

interface SchedulingLinkAvailabilityListProps {
  meetingTimesData: MeetingTimesData;
  i18n: I18n;
}

/*
 * Component.
 */

export const SchedulingLinkAvailabilityList: FC<SchedulingLinkAvailabilityListProps> = (props) => (
  <I18nProvider i18n={props.i18n}>
    {/* Declare styles inline so they become part of the message body. */}
    {/* <div style={{marginTop: 7, marginBottom: 15, width: 270}}> */}
    {_(props.meetingTimesData.availability.timeSlots)
      .groupBy((timeSlot) => renderDate(timeSlot.start, props.meetingTimesData.timezone))
      .map((timeSlots, date) => renderListForDate(props, date, timeSlots))
      .value()}
    {/* </div> */}
  </I18nProvider>
);

function renderListForDate(
  props: SchedulingLinkAvailabilityListProps,
  date: string,
  timeSlots: SchedulingLinkAvailabilityModel['timeSlots'],
) {
  return (
    <Fragment key={`date-${date}`}>
      <div style={{fontWeight: 'bold'}}>{date}</div>
      <ul>{timeSlots.map((timeSlot) => renderTimeSlot(props, timeSlot))}</ul>
    </Fragment>
  );
}

function renderTimeSlot(
  props: SchedulingLinkAvailabilityListProps,
  timeSlot: SchedulingLinkStandardAvailabilityBlockModel,
) {
  const {timezone, timeFormat} = props.meetingTimesData;
  const url = `${props.meetingTimesData.bookingUrl}/?start=${timeSlot.start}`;
  const startString = renderStartTime(timeSlot.start, timezone, timeFormat);
  const endString = renderEndTime(timeSlot.end, timezone, timeFormat);
  return (
    <li key={`slot-${timeSlot.start}`}>
      <a href={url}>
        <Trans id="composer-scheduling-availabilityblock">
          {startString} - {endString}
        </Trans>
      </a>
    </li>
  );
}

function renderDate(timestamp: number, timeZone: string) {
  return DateTime.fromMillis(timestamp).toLocaleString({
    weekday: 'long',
    month: 'long',
    day: '2-digit',
    timeZone,
  });
}

function renderStartTime(timestamp: number, timeZone: string, timeFormat: TimeFormatsEnum) {
  return DateTime.fromMillis(timestamp).toLocaleString({
    hour: 'numeric',
    minute: '2-digit',
    timeZone,
    ...build12Or24hOptionsForFormat(timeFormat),
  });
}

function renderEndTime(timestamp: number, timeZone: string, timeFormat: TimeFormatsEnum) {
  return DateTime.fromMillis(timestamp).toLocaleString({
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
    timeZone,
    ...build12Or24hOptionsForFormat(timeFormat),
  });
}
